import React, { useEffect, useState, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { Spin, Alert, Button, Table, Tag } from 'antd'
import { PrinterOutlined, FileTextOutlined, LinkOutlined, PictureOutlined, CheckCircleOutlined } from '@ant-design/icons'
import {
  formatPhoneNumber,
  formatPaymentTerms,
  amountDollarsToCents,
  buildEstimatePdfSummary,
  jobPhotoReportCaption,
} from '@serviceflow/shared'
import { env } from '../config/environment'
import { AffirmFinancingBlock } from '../components/financing/AffirmFinancingBlock'
import { LineItemNameWithThumbnail } from '../components/LineItemThumbnail'

// Use centralized environment config - strips /api suffix for public routes
const API_URL = env.apiBaseUrl.replace(/\/api$/, '')

interface PDFTemplateSettings {
  primaryColor: string
  fontFamily: 'default' | 'serif' | 'sans-serif' | 'modern'
  layoutStyle: 'classic' | 'modern' | 'minimal'
  logoPosition: 'left' | 'center' | 'right'
  showBusinessPhone: boolean
  showBusinessEmail: boolean
  showBusinessWebsite: boolean
  showCustomerPhone: boolean
  showServiceLocation: boolean
  showTermsSection: boolean
  showSignatureSection: boolean
  customFooterText: string
}

const defaultPDFSettings: PDFTemplateSettings = {
  primaryColor: '#1890ff',
  fontFamily: 'default',
  layoutStyle: 'classic',
  logoPosition: 'left',
  showBusinessPhone: true,
  showBusinessEmail: true,
  showBusinessWebsite: true,
  showCustomerPhone: true,
  showServiceLocation: true,
  showTermsSection: true,
  showSignatureSection: true,
  customFooterText: '',
}

const getFontFamily = (fontFamily: string) => {
  switch (fontFamily) {
    case 'serif':
      return 'Georgia, "Times New Roman", serif'
    case 'sans-serif':
      return 'Arial, Helvetica, sans-serif'
    case 'modern':
      return '"Inter", "Segoe UI", sans-serif'
    default:
      return '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
  }
}

function renderEstimateFinancingButton(
  url: string | number | undefined | null,
  accentColor: string,
  label?: string
): React.ReactNode {
  const href = typeof url === 'string' ? url.trim() : ''
  if (!href) return null
  return (
    <a
      href={href}
      className="no-print"
      style={{
        display: 'inline-block',
        padding: '8px 14px',
        background: '#fff',
        color: accentColor,
        border: `1px solid ${accentColor}`,
        borderRadius: 6,
        textDecoration: 'none',
        textAlign: 'center',
        fontWeight: 600,
        fontSize: 13,
        maxWidth: 300,
        width: '100%',
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        lineHeight: 1.35,
      }}
    >
      {label ? `Request Financing — ${label}` : 'Request Financing'}
    </a>
  )
}

// Helper function to detect and render URLs and images in text
const renderNotesWithMedia = (notes: string) => {
  const urlRegex = /(https?:\/\/[^\s]+)/g
  const parts = notes.split(urlRegex)
  const imageExtensions = /\.(jpg|jpeg|png|gif|webp|bmp|svg)(\?|$)/i
  
  return parts.map((part, index) => {
    if (urlRegex.test(part)) {
      urlRegex.lastIndex = 0
      
      if (imageExtensions.test(part)) {
        return (
          <div key={index} style={{ marginTop: 12, marginBottom: 12 }}>
            <a href={part} target="_blank" rel="noopener noreferrer">
              <div style={{ 
                display: 'inline-block',
                border: '1px solid #d9d9d9',
                borderRadius: 4,
                padding: 4,
                background: '#f5f5f5'
              }}>
                <img
                  src={part}
                  alt="Reference image"
                  style={{ 
                    maxWidth: 150,
                    maxHeight: 150,
                    objectFit: 'cover',
                    borderRadius: 4 
                  }}
                />
                <div style={{ 
                  textAlign: 'center', 
                  fontSize: 10, 
                  color: '#666',
                  marginTop: 4 
                }}>
                  <PictureOutlined /> Reference Image
                </div>
              </div>
            </a>
          </div>
        )
      } else {
        return (
          <a 
            key={index}
            href={part} 
            target="_blank" 
            rel="noopener noreferrer"
            style={{ color: '#1890ff', wordBreak: 'break-all' }}
          >
            <LinkOutlined style={{ marginRight: 4 }} />
            {part}
          </a>
        )
      }
    }
    return <span key={index} style={{ whiteSpace: 'pre-wrap' }}>{part}</span>
  })
}

// Helper to parse address that might be JSON or string
const parseAddress = (address: any): { street?: string; city?: string; state?: string; zipCode?: string } | null => {
  if (!address) return null
  
  // If it's already an object
  if (typeof address === 'object' && address !== null) {
    return address
  }
  
  // If it's a string that looks like JSON
  if (typeof address === 'string') {
    // Check if it's JSON
    if (address.trim().startsWith('{')) {
      try {
        return JSON.parse(address)
      } catch {
        // Not valid JSON, return as street
        return { street: address }
      }
    }
    // Plain string address
    return { street: address }
  }
  
  return null
}

// Format business address from potentially JSON or string value
const formatBusinessAddress = (business: any): { street?: string; city?: string; state?: string; zipCode?: string } => {
  // First check if address is a JSON object
  const parsedAddress = parseAddress(business?.address)
  
  if (parsedAddress && (parsedAddress.street || parsedAddress.city)) {
    return parsedAddress
  }
  
  // Fallback to individual fields on business
  return {
    street: typeof business?.address === 'string' && !business.address.startsWith('{') ? business.address : undefined,
    city: business?.city,
    state: business?.state,
    zipCode: business?.zipCode
  }
}

export const PublicEstimatePdf: React.FC = () => {
  const { token } = useParams()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [data, setData] = useState<any>(null)

  useEffect(() => {
    const fetchData = async () => {
      if (!token) {
        setError('No token provided')
        setLoading(false)
        return
      }

      try {
        const response = await fetch(`${API_URL}/api/public/pdf/${token}`)
        const result = await response.json()

        if (!result.success) {
          setError(result.error || 'Failed to load estimate')
          setLoading(false)
          return
        }

        if (result.type !== 'estimate') {
          setError('Invalid link type')
          setLoading(false)
          return
        }

        setData(result)
        setLoading(false)
      } catch (err) {
        console.error('Error fetching public PDF:', err)
        setError('Failed to load estimate. Please try again.')
        setLoading(false)
      }
    }

    fetchData()
  }, [token])

  const pdfSettings = useMemo(() => {
    if (!data?.business?.settings) return defaultPDFSettings
    const settings = typeof data.business.settings === 'string' 
      ? JSON.parse(data.business.settings) 
      : data.business.settings
    return { ...defaultPDFSettings, ...settings?.pdfTemplateSettings }
  }, [data])

  const phoneFormat = useMemo(() => {
    if (!data?.business?.settings) return '(XXX) XXX-XXXX'
    const settings = typeof data.business.settings === 'string'
      ? JSON.parse(data.business.settings)
      : data.business.settings
    return settings?.phoneFormat || '(XXX) XXX-XXXX'
  }, [data])

  const formatPhone = (phone: string | null | undefined) => {
    if (!phone) return ''
    return formatPhoneNumber(phone, phoneFormat)
  }

  const formatDate = (date: string | Date | null | undefined) => {
    if (!date) return ''
    return new Date(date).toLocaleDateString()
  }

  const formatCurrency = (amount: string | number | undefined | null) => {
    if (amount === undefined || amount === null) return '$0.00'
    return `$${Number(amount).toFixed(2)}`
  }

  const handlePrint = () => {
    window.print()
  }

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        background: '#f5f5f5'
      }}>
        <Spin size="large" tip="Loading estimate..." />
      </div>
    )
  }

  if (error) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        background: '#f5f5f5',
        padding: 24
      }}>
        <Alert 
          type="error" 
          message="Error" 
          description={error}
          showIcon
        />
      </div>
    )
  }

  const { estimate, customer, business, jobPhotos: jobPhotosRaw } = data
  const jobPhotos = Array.isArray(jobPhotosRaw) ? jobPhotosRaw : []

  // Determine if approval/decline is possible
  const canApprove = estimate.status === 'sent' || estimate.status === 'viewed'

  const declineUrl = estimate.approveOption1Url
    ? estimate.approveOption1Url.replace('/approve-public', '/decline-public').replace(/&option=\d/, '')
    : null

  // Get pricing breakdown for selected option (server-stored + reconciled display)
  const selectedOpt = estimate.selectedOption || 1
  const summary = buildEstimatePdfSummary({
    items: estimate.items || [],
    subtotal: estimate.subtotal,
    taxAmount: estimate.taxAmount,
    taxRate: estimate.taxRate,
    totalAmount: estimate.totalAmount,
    markupPercent:
      selectedOpt === 1
        ? estimate.option1MarkupPercent
        : selectedOpt === 2
          ? estimate.option2MarkupPercent
          : estimate.option3MarkupPercent,
    discountPercent:
      selectedOpt === 1
        ? estimate.option1DiscountPercent
        : selectedOpt === 2
          ? estimate.option2DiscountPercent
          : estimate.option3DiscountPercent,
    depositRequired: estimate.depositRequired,
    depositAmount: estimate.depositAmount
  })
  const {
    itemsSubtotal,
    markupPercent: safeMarkupPercent,
    markupAmount,
    discountPercent: safeDiscountPercent,
    discountAmount,
    subtotal: subtotalAfterDiscount,
    taxAmount,
    total,
    amountDue,
    amountDueLabel,
    showMarkup,
    showDiscount
  } = summary

  const itemColumns = [
    {
      title: 'Item',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: any) => (
        <LineItemNameWithThumbnail
          imageUrl={record.imageUrl}
          name={<div style={{ fontWeight: 500 }}>{text}</div>}
          description={
            record.description ? (
              <div style={{ color: '#666', fontSize: '12px' }}>{record.description}</div>
            ) : undefined
          }
        />
      )
    },
    {
      title: 'Qty',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 80,
      align: 'center' as const
    },
    {
      title: 'Unit Price',
      dataIndex: 'unitPrice',
      key: 'unitPrice',
      width: 120,
      align: 'right' as const,
      render: (value: number) => formatCurrency(value)
    },
    {
      title: 'Total',
      key: 'total',
      width: 120,
      align: 'right' as const,
      render: (_: any, record: any) => {
        const lineTotal = record.totalPrice || (record.quantity * record.unitPrice)
        return <strong>{formatCurrency(lineTotal)}</strong>
      }
    }
  ]

  return (
    <>
      <style>{`
        @media print {
          .no-print {
            display: none !important;
          }
          .print-container {
            padding: 0 !important;
            margin: 0 !important;
            background: white !important;
          }
          .estimate-document {
            box-shadow: none !important;
            border: none !important;
            padding: 20px !important;
          }
          body {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
        }
        @media screen {
          .print-container {
            background: #f5f5f5;
            min-height: 100vh;
            padding: 16px;
          }
        }
        @media screen and (max-width: 768px) {
          .print-container {
            padding: 8px;
          }
          .estimate-document {
            padding: 16px !important;
          }
          .pdf-header {
            flex-direction: column !important;
            text-align: center !important;
          }
          .pdf-header > div {
            order: unset !important;
            text-align: center !important;
            margin-bottom: 16px !important;
          }
          .totals-section {
            width: 100% !important;
          }
          .signature-section {
            flex-direction: column !important;
            gap: 24px !important;
          }
          .option-cards-grid {
            grid-template-columns: 1fr !important;
          }
          .job-photo-card {
            break-inside: avoid;
            page-break-inside: avoid;
          }
          .ant-table {
            font-size: 12px;
          }
          .ant-table-thead > tr > th,
          .ant-table-tbody > tr > td {
            padding: 8px 4px !important;
          }
        }
      `}</style>

      <div className="print-container">
        {/* Header with print & approve buttons */}
        <div className="no-print" style={{ 
          maxWidth: 900, 
          margin: '0 auto 16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 8
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <FileTextOutlined style={{ fontSize: 24, color: pdfSettings.primaryColor }} />
            <span style={{ fontSize: 18, fontWeight: 600 }}>Estimate #{estimate.estimateNumber}</span>
            <Tag color={estimate.status === 'approved' ? 'green' : estimate.status === 'declined' ? 'red' : 'blue'}>
              {estimate.status?.toUpperCase()}
            </Tag>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Button 
              type="primary" 
              icon={<PrinterOutlined />}
              onClick={handlePrint}
              style={{ backgroundColor: pdfSettings.primaryColor, borderColor: pdfSettings.primaryColor }}
            >
              Print / Save PDF
            </Button>
            {canApprove && !estimate.hasMultipleOptions && estimate.approveOption1Url && (
              <>
                <Button
                  type="primary"
                  icon={<CheckCircleOutlined />}
                  href={estimate.approveOption1Url}
                  style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                >
                  Approve Estimate
                </Button>
                {declineUrl && (
                  <Button
                    danger
                    href={declineUrl}
                  >
                    Decline
                  </Button>
                )}
              </>
            )}
          </div>
        </div>

        {/* Document */}
        <div 
          className="estimate-document"
          style={{
            maxWidth: 900,
            margin: '0 auto',
            background: 'white',
            padding: 40,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            fontFamily: getFontFamily(pdfSettings.fontFamily)
          }}
        >
          {/* Header */}
          {(() => {
            const businessAddr = formatBusinessAddress(business)
            return (
              <div 
                className="pdf-header"
                style={{ 
                  display: 'flex', 
                  justifyContent: pdfSettings.logoPosition === 'center' ? 'center' : 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: 40,
                  flexDirection: pdfSettings.logoPosition === 'center' ? 'column' : 'row',
                  textAlign: pdfSettings.logoPosition === 'center' ? 'center' : 'left',
                  gap: 16,
                  flexWrap: 'wrap'
                }}>
                <div style={{ 
                  order: pdfSettings.logoPosition === 'right' ? 2 : 1,
                  marginBottom: pdfSettings.logoPosition === 'center' ? 20 : 0,
                  minWidth: 200
                }}>
                  {business?.logo ? (
                    <img 
                      src={business.logo} 
                      alt={business.name} 
                      style={{ maxHeight: 80, maxWidth: 200 }}
                    />
                  ) : (
                    <h1 style={{ 
                      margin: 0, 
                      fontSize: 24, 
                      color: pdfSettings.primaryColor 
                    }}>
                      {business?.name}
                    </h1>
                  )}
                  <div style={{ marginTop: 8, color: '#666', fontSize: 14 }}>
                    {businessAddr.street && <div>{businessAddr.street}</div>}
                    {(businessAddr.city || businessAddr.state || businessAddr.zipCode) && (
                      <div>
                        {[businessAddr.city, businessAddr.state].filter(Boolean).join(', ')} {businessAddr.zipCode}
                      </div>
                    )}
                    {pdfSettings.showBusinessPhone && business?.phone && (
                      <div>{formatPhone(business.phone)}</div>
                    )}
                    {pdfSettings.showBusinessEmail && business?.email && (
                      <div>{business.email}</div>
                    )}
                  </div>
                </div>

                <div style={{ 
                  order: pdfSettings.logoPosition === 'right' ? 1 : 2,
                  textAlign: pdfSettings.logoPosition === 'center' ? 'center' : 'right',
                  minWidth: 200
                }}>
                  <h2 style={{ 
                    margin: 0, 
                    fontSize: 28, 
                    color: pdfSettings.primaryColor,
                    textTransform: 'uppercase',
                    letterSpacing: 2
                  }}>
                    Estimate
                  </h2>
                  <div style={{ marginTop: 8, fontSize: 14 }}>
                    <div><strong>Estimate #:</strong> {estimate.estimateNumber}</div>
                    <div><strong>Date:</strong> {formatDate(estimate.createdAt)}</div>
                    {estimate.validUntil && (
                      <div><strong>Valid Until:</strong> {formatDate(estimate.validUntil)}</div>
                    )}
                    {estimate.projectType && (
                      <div><strong>Project Type:</strong> {estimate.projectType}</div>
                    )}
                  </div>
                </div>
              </div>
            )
          })()}

          {/* Customer Info */}
          <div style={{ 
            marginBottom: 30,
            padding: 20,
            background: '#fafafa',
            borderRadius: 4
          }}>
            <h3 style={{ 
              margin: '0 0 12px 0', 
              fontSize: 14, 
              color: pdfSettings.primaryColor,
              textTransform: 'uppercase',
              letterSpacing: 1
            }}>
              Bill To
            </h3>
            <div style={{ fontSize: 14 }}>
              <div style={{ fontWeight: 600 }}>
                {customer?.firstName} {customer?.lastName}
              </div>
              {customer?.address && <div>{customer.address}</div>}
              {(customer?.city || customer?.state || customer?.zipCode) && (
                <div>
                  {[customer?.city, customer?.state].filter(Boolean).join(', ')} {customer?.zipCode}
                </div>
              )}
              {pdfSettings.showCustomerPhone && customer?.phone && (
                <div>{formatPhone(customer.phone)}</div>
              )}
              {customer?.email && <div>{customer.email}</div>}
            </div>
          </div>

          {/* Service Location */}
          {pdfSettings.showServiceLocation && estimate.serviceAddress && (
            <div style={{ marginBottom: 30 }}>
              <h3 style={{ 
                margin: '0 0 8px 0', 
                fontSize: 14, 
                color: pdfSettings.primaryColor,
                textTransform: 'uppercase',
                letterSpacing: 1
              }}>
                Service Location
              </h3>
              <div style={{ fontSize: 14, color: '#666' }}>
                {estimate.serviceAddress}
                {(estimate.serviceCity || estimate.serviceState || estimate.serviceZip) && (
                  <span>, {[estimate.serviceCity, estimate.serviceState, estimate.serviceZip].filter(Boolean).join(', ')}</span>
                )}
              </div>
            </div>
          )}

          {/* Title & Description */}
          {(estimate.title || estimate.description) && (
            <div style={{ marginBottom: 30 }}>
              {estimate.title && (
                <h3 style={{ margin: '0 0 8px 0', fontSize: 18 }}>{estimate.title}</h3>
              )}
              {estimate.description && (
                <p style={{ margin: 0, color: '#666' }}>{estimate.description}</p>
              )}
            </div>
          )}

          {/* Items Table */}
          {estimate.items && estimate.items.length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <h3 style={{ 
                margin: '0 0 12px 0', 
                fontSize: 14, 
                color: '#333',
                textTransform: 'uppercase',
                letterSpacing: 0.5
              }}>
                Items & Services
              </h3>
              <Table
                dataSource={estimate.items}
                columns={itemColumns}
                pagination={false}
                rowKey="id"
                size="small"
                bordered
              />
            </div>
          )}

          {/* Pricing Options (Multi-option display) */}
          {estimate.hasMultipleOptions ? (
            <div style={{ marginBottom: 30 }}>
              <h3 style={{ 
                margin: '0 0 15px 0', 
                fontSize: 14, 
                color: '#333',
                textTransform: 'uppercase',
                letterSpacing: 0.5
              }}>
                Pricing Options
              </h3>
              <div 
                className="option-cards-grid"
                style={{ 
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: 12
                }}
              >
                {[1, 2, 3].map((optNum) => {
                  const optTitle = estimate[`option${optNum}Title`] || `Option ${optNum}`
                  const optDesc = estimate[`option${optNum}Description`]
                  const optSubtotal = estimate[`option${optNum}Subtotal`]
                  const optTaxAmount = estimate[`option${optNum}TaxAmount`]
                  const optTotal = estimate[`option${optNum}Total`]
                  const isSelected = selectedOpt === optNum
                  const approveUrl = estimate[`approveOption${optNum}Url`]
                  const financingUrl = estimate[`financingLinkOption${optNum}`]

                  if (!optTotal && optTotal !== 0) return null

                  return (
                    <div 
                      key={optNum}
                      style={{
                        border: isSelected ? `2px solid ${pdfSettings.primaryColor}` : '1px solid #e8e8e8',
                        borderRadius: 8,
                        padding: 16,
                        background: isSelected ? `${pdfSettings.primaryColor}08` : '#fff',
                        display: 'flex',
                        flexDirection: 'column'
                      }}
                    >
                      <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>
                        {optTitle}
                      </div>
                      {optDesc && (
                        <div style={{ color: '#666', fontSize: 13, marginBottom: 10, flex: 1 }}>
                          {optDesc}
                        </div>
                      )}
                      <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>
                        Subtotal: {formatCurrency(optSubtotal)} | Tax: {formatCurrency(optTaxAmount)}
                      </div>
                      <div style={{ 
                        fontSize: 22, 
                        fontWeight: 700, 
                        color: pdfSettings.primaryColor,
                        marginBottom: 8
                      }}>
                        {formatCurrency(optTotal)}
                      </div>
                      {isSelected && (
                        <Tag color="green" style={{ alignSelf: 'flex-start', marginBottom: 8 }}>Selected</Tag>
                      )}
                      {((canApprove && approveUrl) || financingUrl) ? (
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          {canApprove && approveUrl && (
                          <a 
                            href={approveUrl}
                            className="no-print"
                            style={{
                              display: 'inline-block',
                              padding: '8px 16px',
                              background: '#52c41a',
                              color: '#fff',
                              borderRadius: 6,
                              textDecoration: 'none',
                              textAlign: 'center',
                              fontWeight: 600,
                              fontSize: 14
                            }}
                          >
                            <CheckCircleOutlined style={{ marginRight: 6 }} />
                            Approve {optTitle}
                          </a>
                          )}
                          {renderEstimateFinancingButton(
                            financingUrl,
                            pdfSettings.primaryColor,
                            optTitle
                          )}
                          {canApprove && declineUrl && (
                            <a
                              href={declineUrl}
                              className="no-print"
                              style={{
                                display: 'inline-block',
                                padding: '8px 16px',
                                background: '#fff',
                                color: '#dc3545',
                                border: '1px solid #dc3545',
                                borderRadius: 6,
                                textDecoration: 'none',
                                textAlign: 'center',
                                fontWeight: 600,
                                fontSize: 14
                              }}
                            >
                              Decline
                            </a>
                          )}
                        </div>
                      ) : null}
                    </div>
                  )
                })}
              </div>
            </div>
          ) : (
            /* Single-option totals with markup/discount breakdown */
            <div style={{ 
              display: 'flex', 
              justifyContent: 'flex-end',
              marginBottom: 30
            }}>
              <div className="totals-section" style={{ width: 300, maxWidth: '100%' }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  padding: '8px 0',
                  borderBottom: '1px solid #eee'
                }}>
                  <span>Items Subtotal:</span>
                  <span>{formatCurrency(itemsSubtotal)}</span>
                </div>
                {showMarkup && (
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    padding: '8px 0',
                    borderBottom: '1px solid #eee'
                  }}>
                    <span>Markup ({(safeMarkupPercent * 100).toFixed(1)}%):</span>
                    <span>+{formatCurrency(markupAmount)}</span>
                  </div>
                )}
                {showDiscount && (
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    padding: '8px 0',
                    borderBottom: '1px solid #eee',
                    color: '#52c41a'
                  }}>
                    <span>
                      Discount
                      {safeDiscountPercent > 0
                        ? ` (${(safeDiscountPercent * 100).toFixed(1)}%)`
                        : ''}
                      :
                    </span>
                    <span>-{formatCurrency(discountAmount)}</span>
                  </div>
                )}
                {(showMarkup || showDiscount) && (
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    padding: '8px 0',
                    borderBottom: '1px solid #eee'
                  }}>
                    <span>Subtotal:</span>
                    <span>{formatCurrency(subtotalAfterDiscount)}</span>
                  </div>
                )}
                {taxAmount > 0 && (
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    padding: '8px 0',
                    borderBottom: '1px solid #eee'
                  }}>
                    <span>Tax{estimate.taxRate ? ` (${(Number(estimate.taxRate) * 100).toFixed(2)}%)` : ''}:</span>
                    <span>+{formatCurrency(taxAmount)}</span>
                  </div>
                )}
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  padding: '10px 0',
                  borderBottom: '1px solid #eee',
                  fontWeight: 600
                }}>
                  <span>Estimate Total:</span>
                  <span>{formatCurrency(total)}</span>
                </div>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  padding: '12px 0',
                  fontWeight: 700,
                  fontSize: 18,
                  color: pdfSettings.primaryColor,
                  borderTop: '2px solid #333',
                  marginTop: 4
                }}>
                  <span>{amountDueLabel}:</span>
                  <span>{formatCurrency(amountDue)}</span>
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
                  {renderEstimateFinancingButton(
                    estimate.financingLink,
                    pdfSettings.primaryColor
                  )}
                </div>
              </div>
            </div>
          )}

          {canApprove && token && (
            <div style={{ marginTop: 24, maxWidth: 480 }}>
              <AffirmFinancingBlock
                amountCents={amountDollarsToCents(amountDue)}
                pdfToken={token}
                source="public_estimate_pdf"
                compact
              />
            </div>
          )}

          {/* Job-linked photos (from files on the linked job) */}
          {jobPhotos.length > 0 && (
            <div className="job-photos-section" style={{ marginBottom: 30 }}>
              <h3
                style={{
                  margin: '0 0 12px 0',
                  fontSize: 14,
                  color: pdfSettings.primaryColor,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                }}
              >
                {Array.isArray(estimate.attachedPhotoIds) && estimate.attachedPhotoIds.length > 0
                  ? 'Attached photos'
                  : 'Photos from job'}
              </h3>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
                  gap: 12,
                }}
              >
                {jobPhotos.map((photo: { id: string; url: string; description?: string | null; jobPhotoCategory?: string | null; category?: string | null; originalName?: string }) => {
                  const caption = jobPhotoReportCaption(photo)
                  return (
                    <div
                      key={photo.id}
                      className="job-photo-card"
                      style={{
                        border: '1px solid #e8e8e8',
                        borderRadius: 6,
                        overflow: 'hidden',
                        background: '#fafafa',
                      }}
                    >
                      <a href={photo.url} target="_blank" rel="noopener noreferrer">
                        <img
                          src={photo.url}
                          alt={caption || 'Job photo'}
                          style={{
                            width: '100%',
                            height: 180,
                            objectFit: 'cover',
                            display: 'block',
                            verticalAlign: 'top',
                          }}
                        />
                      </a>
                      {caption ? (
                        <div
                          style={{
                            padding: 8,
                            fontSize: 12,
                            color: '#555',
                            wordBreak: 'break-word',
                          }}
                        >
                          {caption}
                        </div>
                      ) : null}
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* Deposit Info */}
          {estimate.depositRequired && Number(estimate.depositAmount) > 0 && (
            <div style={{ 
              marginBottom: 30,
              padding: 16,
              background: '#fff7e6',
              borderRadius: 4,
              border: '1px solid #ffd591'
            }}>
              <h4 style={{ margin: '0 0 8px 0', color: '#d46b08' }}>
                Deposit Required
              </h4>
              <div style={{ fontSize: 16, fontWeight: 700 }}>
                {formatCurrency(estimate.depositAmount)} deposit due upon acceptance
              </div>
              {(() => {
                const rawSettings = data?.business?.settings;
                const settings = typeof rawSettings === 'string' ? JSON.parse(rawSettings || '{}') : (rawSettings || {});
                const feePercent = settings.creditCardProcessingFeePercent;
                const depositAmount = Number(estimate.depositAmount) || 0;
                const feeAmount = feePercent && depositAmount > 0 ? (depositAmount * feePercent) / 100 : 0;
                if (feePercent && feePercent > 0) {
                  return (
                    <div style={{
                      marginTop: 12,
                      padding: 10,
                      background: '#e6f7ff',
                      border: '1px solid #91d5ff',
                      borderRadius: 4,
                      fontSize: 13,
                      color: '#0050b3'
                    }}>
                      <strong>Credit card processing fee:</strong>{' '}
                      Credit card payments are subject to a {feePercent}% processing fee
                      {feeAmount > 0 ? ` (e.g. $${feeAmount.toFixed(2)} on a deposit of $${depositAmount.toFixed(2)})` : ''}.
                      ACH/bank transfer payments do not include this fee.
                    </div>
                  );
                }
                return null;
              })()}
            </div>
          )}

          {/* Payment Terms */}
          {estimate.paymentTerms && (
            <div style={{ marginBottom: 20 }}>
              <strong>Payment Terms:</strong> {formatPaymentTerms(estimate.paymentTerms)}
            </div>
          )}

          {/* Terms & Conditions */}
          {pdfSettings.showTermsSection && (estimate.terms || estimate.workWarranty || estimate.materialsIncluded || estimate.exclusions || estimate.changeOrderPolicy || estimate.specialInstructions) && (
            <div style={{ 
              marginBottom: 30,
              padding: 16,
              background: '#fafafa',
              borderRadius: 4
            }}>
              <h4 style={{ margin: '0 0 12px 0', color: pdfSettings.primaryColor }}>
                Terms & Conditions
              </h4>
              {estimate.terms && (
                <div style={{ marginBottom: 10 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#666', marginBottom: 3 }}>Terms</div>
                  <div style={{ fontSize: 13, color: '#333', whiteSpace: 'pre-wrap' }}>{estimate.terms}</div>
                </div>
              )}
              {estimate.workWarranty && (
                <div style={{ marginBottom: 10 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#666', marginBottom: 3 }}>Work Warranty</div>
                  <div style={{ fontSize: 13, color: '#333', whiteSpace: 'pre-wrap' }}>{estimate.workWarranty}</div>
                </div>
              )}
              {estimate.materialsIncluded && (
                <div style={{ marginBottom: 10 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#666', marginBottom: 3 }}>Materials Included</div>
                  <div style={{ fontSize: 13, color: '#333', whiteSpace: 'pre-wrap' }}>{estimate.materialsIncluded}</div>
                </div>
              )}
              {estimate.exclusions && (
                <div style={{ marginBottom: 10 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#666', marginBottom: 3 }}>Exclusions</div>
                  <div style={{ fontSize: 13, color: '#333', whiteSpace: 'pre-wrap' }}>{estimate.exclusions}</div>
                </div>
              )}
              {estimate.changeOrderPolicy && (
                <div style={{ marginBottom: 10 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#666', marginBottom: 3 }}>Change Order Policy</div>
                  <div style={{ fontSize: 13, color: '#333', whiteSpace: 'pre-wrap' }}>{estimate.changeOrderPolicy}</div>
                </div>
              )}
              {estimate.specialInstructions && (
                <div style={{ marginBottom: 10 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#666', marginBottom: 3 }}>Special Instructions</div>
                  <div style={{ fontSize: 13, color: '#333', whiteSpace: 'pre-wrap' }}>{estimate.specialInstructions}</div>
                </div>
              )}
            </div>
          )}

          {/* Additional Notes */}
          {estimate.additionalNotes && (
            <div style={{ 
              marginBottom: 30,
              padding: 16,
              background: '#fffbe6',
              borderRadius: 4,
              border: '1px solid #ffe58f'
            }}>
              <h4 style={{ margin: '0 0 8px 0' }}>Additional Notes</h4>
              <div style={{ fontSize: 13 }}>
                {renderNotesWithMedia(estimate.additionalNotes)}
              </div>
            </div>
          )}

          {/* Signature Section */}
          {pdfSettings.showSignatureSection && (
            <div 
              className="signature-section"
              style={{ 
                display: 'flex',
                justifyContent: 'space-between',
                marginTop: 60,
                gap: 40
              }}>
              <div style={{ flex: 1, minWidth: 150 }}>
                <div style={{ borderBottom: '1px solid #333', marginBottom: 8, height: 40 }} />
                <div style={{ fontSize: 12, color: '#666' }}>Customer Signature</div>
              </div>
              <div style={{ flex: 1, minWidth: 150 }}>
                <div style={{ borderBottom: '1px solid #333', marginBottom: 8, height: 40 }} />
                <div style={{ fontSize: 12, color: '#666' }}>Date</div>
              </div>
            </div>
          )}

          {/* Footer */}
          <div style={{ 
            marginTop: 40,
            paddingTop: 20,
            borderTop: '1px solid #eee',
            textAlign: 'center',
            fontSize: 12,
            color: '#888'
          }}>
            {pdfSettings.customFooterText ? (
              pdfSettings.customFooterText
            ) : (
              <>
                Thank you for considering {business?.name || 'us'} for your project!
                <br />
                <span style={{ fontSize: 11 }}>Please contact us if you have any questions about this estimate.</span>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

